import React, { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/Modal';
import Button from '@/components/Button';
import SearchInput from '@/components/SearchInput';
import { TTableSelection } from '@/components/DataTable';
import { TWalletData } from '@/features/home/types/WalletData';
import { addFavoriteAccountsModal } from '@/utils/translations';
import AddFavoriteAccountsTable from './AddFavoriteAccountsTable';

interface AddFavoriteAccountsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Replaces the favorites set entirely with whatever is checked on Apply. */
  onApply: (walletIds: string[]) => void;
  /** All of the user's wallets, not just unfavorited ones - already-favorited rows are shown too,
   * pre-checked, so this doubles as an "add or remove" picker rather than an add-only one. */
  wallets: TWalletData[];
  favoriteAccountIds: string[];
}

const walletMatchesSearch = (wallet: TWalletData, query: string): boolean =>
  [wallet.name, wallet.accountNumber].some((value) => value?.toLowerCase().includes(query));

const AddFavoriteAccountsModal: React.FC<AddFavoriteAccountsModalProps> = ({
  isOpen,
  onClose,
  onApply,
  wallets,
  favoriteAccountIds
}) => {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  // Re-seed from the current favorites (and clear any leftover search) every time the modal
  // opens, so already-favorited accounts show up pre-checked instead of a blank slate.
  useEffect(() => {
    if (isOpen) {
      setSelectedKeys(new Set(favoriteAccountIds));
      setSearch('');
    }
  }, [isOpen, favoriteAccountIds]);

  const filteredWallets = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? wallets.filter((wallet) => walletMatchesSearch(wallet, query)) : wallets;
  }, [wallets, search]);

  const handleToggleRow = (wallet: TWalletData) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(wallet.id)) {
        next.delete(wallet.id);
      } else {
        next.add(wallet.id);
      }
      return next;
    });
  };

  // Applies to the currently VISIBLE (filtered) rows, matching the checkbox state DataTable's
  // own header checkbox actually shows - not the full unfiltered wallet list, most of which may
  // not even be on screen while a search is active.
  const handleToggleAll = () => {
    setSelectedKeys((prev) => {
      const allFilteredSelected = filteredWallets.every((wallet) => prev.has(wallet.id));
      const next = new Set(prev);
      filteredWallets.forEach((wallet) =>
        allFilteredSelected ? next.delete(wallet.id) : next.add(wallet.id)
      );
      return next;
    });
  };

  const handleApply = () => {
    onApply(Array.from(selectedKeys));
    onClose();
  };

  const selection: TTableSelection<TWalletData> = {
    selectedKeys,
    onToggleRow: handleToggleRow,
    onToggleAll: handleToggleAll
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      headerText={addFavoriteAccountsModal.title}
      headerVariant="lg">
      {/*
        Every direct child here is `shrink-0` (description, search, the table's reserved-height
        wrapper, Apply/Close) so none of them give way under pressure - that's deliberate, it's
        what keeps the table area a constant size while a search is typed instead of jumping
        around. The trade-off: on a short enough window, their combined height can still exceed
        max-h-[70vh] with nothing able to shrink to compensate - overflow-y-auto here is the
        fallback for exactly that case, so the whole modal body scrolls to reach Apply/Close
        rather than them landing off-screen with no way to reach them. It has no effect at all on
        a normal-sized window, where everything already fits and nothing scrolls.
      */}
      <div className="flex flex-col gap-y-4 py-4 w-[400px] max-h-[75vh] overflow-y-auto vertical-scroll">
        <p className="text-sm font-medium text-dark-navy-30 shrink-0">
          {addFavoriteAccountsModal.description}
        </p>

        {wallets.length ? (
          <>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={addFavoriteAccountsModal.searchPlaceholder}
              aria-label="Search accounts"
              containerClassName="shrink-0"
            />

            {/* DataTable's own maxBodyHeight is deliberately adaptive (a cap, not a fixed
                size - see DataTable.tsx) so the table itself shrinks for a short/empty
                result. That's the right behaviour for the table, but left alone here it means
                the Apply/Close row below moves with it on every keystroke. This wrapper
                reserves a constant amount of space instead - the table's own header (outside
                its scroll area) plus its 40vh-capped body, whichever it actually renders at -
                so the table can still shrink inside it, but the buttons below never move.
                overflow-hidden is a safety margin, not the mechanism: the buffer above is
                already sized to fit the header, so nothing should actually be clipped. */}
            <div className="shrink-0 overflow-hidden h-[calc(36vh_+_3.5rem)]">
              <AddFavoriteAccountsTable
                wallets={filteredWallets}
                selection={selection}
                emptyMessage={addFavoriteAccountsModal.noSearchResults}
                maxBodyHeight="36vh"
              />
            </div>
          </>
        ) : (
          <p className="text-xs font-medium text-aux-gray-2 py-6 text-center shrink-0">
            {addFavoriteAccountsModal.noAccountsAvailable}
          </p>
        )}

        <div className="flex items-center justify-center gap-x-5 shrink-0">
          <Button
            size="pill"
            className="w-[140px]"
            disabled={wallets.length === 0}
            onClick={handleApply}>
            {addFavoriteAccountsModal.applyButton}
          </Button>
          <Button
            variant="primary_inverted"
            size="pill"
            className="w-[140px] border-aux-gray"
            onClick={onClose}>
            {addFavoriteAccountsModal.closeButton}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddFavoriteAccountsModal;
